// Device-owned history and a durable outbox. This store is deliberately excluded from cloud sync.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '../i18n';
import { nanoid } from 'nanoid/non-secure';
import { persistStorage } from './storage';
import * as api from '../platform/social';
import { cacheRemotePhoto, deletePhoto, savePhoto, storedPhoto, type PickedPhoto } from '../platform/media';
import type { FriendPost, FriendsOverview, MediaRef } from '../types';

type Outgoing = {
  id: string;
  caption: string;
  recipe: unknown;
  media?: MediaRef;
  replyTo?: string;
  created?: {
    id: string;
    threadId: string;
    createdAt: number;
    updatedAt: number;
    replyDueDay: string | null;
    public?: boolean;
  }[];
};
type State = {
  names: Record<string, string>;
  since: number;
  syncing: boolean;
  generation: number;
  owner: string | null;
  overview: FriendsOverview | null;
  posts: Record<string, FriendPost[]>;
  outbox: Outgoing[];
  acknowledgements: Record<string, number>;
  busy: boolean;
  error: string | null;
  receive: (incoming: FriendPost, owner: string | null, generation: number) => Promise<void>;
  sync: () => Promise<void>;
  refresh: () => Promise<void>;
  load: (thread: string, before?: number) => Promise<number | null>;
  send: (caption: string, recipe: unknown, photo: PickedPhoto | null) => Promise<void>;
  reply: (target: string, caption: string) => Promise<void>;
  flush: () => Promise<void>;
  forget: () => void;
};
export const useFriends = create<State>()(
  persist(
    (set, get) => ({
      names: {},
      since: 0,
      syncing: false,
      generation: 0,
      owner: null,
      overview: null,
      posts: {},
      outbox: [],
      acknowledgements: {},
      busy: false,
      error: null,
      refresh: async () => {
        const generation = get().generation;
        try {
          const overview = await api.friendOverview();
          if (get().generation !== generation) return;
          if (get().owner && get().owner !== overview.me) get().forget();
          set(s => ({
            owner: overview.me,
            overview,
            names: { ...s.names, ...Object.fromEntries(overview.threads.map(t => [t.id, t.friend.displayName])) },
            error: null,
          }));
        } catch (e) {
          set({ error: (e as Error).message });
        }
      },
      receive: async (incoming, owner, generation) => {
        const threadId = incoming.threadId;
        const previous = get().posts[threadId]?.find(p => p.id === incoming.id);
        if (previous && previous.updatedAt > incoming.updatedAt) return;
        let localMedia = previous?.localMedia;
        if (incoming.photo && !localMedia) {
          localMedia = await cacheRemotePhoto(api.friendMediaSource(incoming.id), incoming.mediaType);
        }
        if (get().owner !== owner || get().generation !== generation) {
          if (localMedia && localMedia !== previous?.localMedia) await deletePhoto(localMedia);
          return;
        }
        const post = { ...incoming, localMedia };
        // Persist before acknowledging. Missing expired relay records never erase the local history.
        set(s => ({
          posts: {
            ...s.posts,
            [threadId]: [
              ...(s.posts[threadId] ?? []).filter(p => p.id !== post.id),
              ...(post.deletedAt ? [] : [post]),
            ].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id)),
          },
        }));
        if (previous?.localMedia && post.deletedAt) {
          const uri = previous.localMedia.uri;
          const referenced =
            Object.values(get().posts)
              .flat()
              .some(p => p.localMedia?.uri === uri) || get().outbox.some(p => p.media?.uri === uri);
          if (!referenced) await deletePhoto(previous.localMedia);
        }
        if (get().acknowledgements[post.id] !== post.updatedAt) {
          await api.friendAck(post.id, post.updatedAt);
          if (get().owner === owner && get().generation === generation)
            set(s => ({ acknowledgements: { ...s.acknowledgements, [post.id]: post.updatedAt } }));
        }
      },
      sync: async () => {
        if (get().syncing || !get().overview?.started) return;
        const owner = get().owner,
          generation = get().generation;
        set({ syncing: true });
        try {
          let cursor: string | undefined;
          let serverNow = get().since;
          do {
            const page = await api.friendInbox(get().since, cursor);
            if (get().owner !== owner || get().generation !== generation) return;
            for (const incoming of page.posts) await get().receive(incoming, owner, generation);
            cursor = page.next ?? undefined;
            serverNow = page.serverNow;
          } while (cursor);
          if (get().owner === owner && get().generation === generation)
            set({ since: Math.max(get().since, serverNow - 1000) });
        } catch (e) {
          if (get().owner === owner && get().generation === generation) set({ error: (e as Error).message });
        } finally {
          if (get().generation === generation) set({ syncing: false });
        }
      },
      load: async (threadId, before) => {
        const owner = get().owner,
          generation = get().generation;
        try {
          const page = await api.friendPosts(threadId, before);
          if (get().owner !== owner || get().generation !== generation) return null;
          for (const incoming of page.posts) {
            await get().receive(incoming, owner, generation);
          }
          return page.next;
        } catch (e) {
          if (get().owner === owner && get().generation === generation) set({ error: (e as Error).message });
          return null;
        }
      },
      send: async (caption, recipe, photo) => {
        const generation = get().generation;
        const media = photo ? await savePhoto(photo) : undefined;
        if (get().generation !== generation) {
          if (media) await deletePhoto(media);
          return;
        }
        const outgoing = { id: nanoid(), caption, recipe, media };
        set(s => ({ outbox: [...s.outbox, outgoing] }));
        await get().flush();
      },
      reply: async (target, caption) => {
        set(s => ({ outbox: [...s.outbox, { id: nanoid(), caption, recipe: null, replyTo: target }] }));
        await get().flush();
      },
      flush: async () => {
        if (get().busy || !get().overview?.started) return;
        const owner = get().owner,
          generation = get().generation;
        set({ busy: true, error: null });
        try {
          for (const outgoing of get().outbox) {
            if (get().owner !== owner || get().generation !== generation) return;
            // Batch transport only: delivery records and interactions remain one-to-one.
            const created = outgoing.created ?? [];
            if (!outgoing.created) {
              if (outgoing.replyTo)
                created.push(await api.friendReply(outgoing.replyTo, outgoing.caption, outgoing.id));
              else {
                const page = await api.friendSend(outgoing.caption, outgoing.recipe, !!outgoing.media, outgoing.id);
                created.push(...page.posts);
              }
              if (get().owner !== owner || get().generation !== generation) return;
              set(s => ({ outbox: s.outbox.map(p => (p.id === outgoing.id ? { ...p, created } : p)) }));
            }
            // Keep the sender's own content before the recipient can clear its relay copy.
            for (const p of created) {
              if (get().owner !== owner || get().generation !== generation) return;
              if (!get().posts[p.threadId]?.some(existing => existing.id === p.id)) {
                const local: FriendPost = {
                  ...p,
                  authorId: owner!,
                  authorName: i18n.t('streaks.you'),
                  caption: outgoing.caption,
                  recipe: outgoing.recipe,
                  photo: !!outgoing.media,
                  localMedia: outgoing.media,
                  mediaType: null,
                  replyTo: outgoing.replyTo ?? null,
                  repliedAt: null,
                  deletedAt: null,
                };
                set(s => ({ posts: { ...s.posts, [p.threadId]: [...(s.posts[p.threadId] ?? []), local] } }));
              }
            }
            if (outgoing.media && created.length)
              await api.friendUpload(created[0].id, await storedPhoto(outgoing.media));
            if (get().owner !== owner || get().generation !== generation) return;
            set(s => ({ outbox: s.outbox.filter(p => p.id !== outgoing.id) }));
          }
          await get().refresh();
        } catch (e) {
          if (get().owner === owner && get().generation === generation) set({ error: (e as Error).message });
        } finally {
          if (get().generation === generation) set({ busy: false });
        }
      },
      forget: () => {
        const refs = [
          ...Object.values(get().posts)
            .flat()
            .map(p => p.localMedia),
          ...get().outbox.map(p => p.media),
        ].filter((r): r is MediaRef => !!r);
        const unique = new Map(refs.map(r => [r.uri, r]));
        set({
          names: {},
          since: 0,
          syncing: false,
          generation: get().generation + 1,
          busy: false,
          owner: null,
          overview: null,
          posts: {},
          outbox: [],
          acknowledgements: {},
          error: null,
        });
        for (const ref of unique.values()) deletePhoto(ref).catch(() => {});
      },
    }),
    {
      name: 'friend-relay',
      storage: persistStorage,
      partialize: s =>
        ({
          names: s.names,
          overview: s.overview,
          since: s.since,
          owner: s.owner,
          posts: s.posts,
          outbox: s.outbox,
          acknowledgements: s.acknowledgements,
        } as Partial<State>),
    },
  ),
);
